import { workflowApiRequest } from './apiClient';
import type { ApiResult } from './types';

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const bool = (v: unknown) => (typeof v === 'boolean' ? v : undefined);

export const LETTER_TEMPLATE_NAME_MAX_LENGTH = 200;

export type LetterTemplateListRow = {
  id: string;
  categoryId: string;
  categoryName: string;
  subCategoryId: string;
  subCategoryName: string;
  templateName: string;
  active: boolean;
  updatedAt: string;
};

export type LetterTemplateDetail = {
  id: string;
  code: string;
  categoryId: string;
  categoryName: string;
  subCategoryId: string;
  subCategoryName: string;
  templateName: string;
  description: string;
  active: boolean;
  updatedAt: string;
  updatedBy: string;
  letterContent: string;
  pdfUrl: string;
  pdfBase64: string;
  htmlContent: string;
};

export type LetterTemplateListParams = {
  page: number;
  pageSize: number;
  categoryId?: string;
  subCategoryId?: string;
  templateName?: string;
  /** When omitted, list includes all statuses. */
  status?: boolean;
  sortBy?: string;
  sortDir?: 'ASC' | 'DESC';
  signal?: AbortSignal;
};

export type LetterTemplateListResult = {
  items: LetterTemplateListRow[];
  total: number;
  totalPages: number;
};

export type UpdateLetterTemplateInput = {
  templateName: string;
  active: boolean;
};

type ApiRow = Record<string, unknown>;

function pickStr(o: ApiRow, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function unwrapRow(data: unknown): ApiRow {
  if (!data || typeof data !== 'object') return {};
  const record = data as ApiRow;
  const nested = record.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as ApiRow;
  }
  return record;
}

function unwrapRows(data: unknown): ApiRow[] {
  if (Array.isArray(data)) return data as ApiRow[];
  if (!data || typeof data !== 'object') return [];
  const record = data as ApiRow;
  if (Array.isArray(record.content)) return record.content as ApiRow[];
  if (Array.isArray(record.data)) return record.data as ApiRow[];
  if (Array.isArray(record.items)) return record.items as ApiRow[];
  const nested = record.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const inner = nested as ApiRow;
    if (Array.isArray(inner.content)) return inner.content as ApiRow[];
    if (Array.isArray(inner.data)) return inner.data as ApiRow[];
  }
  return [];
}

function pageMeta(data: unknown): { total: number; totalPages: number } | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const record = data as ApiRow;
  const sources = [record];
  const nested = record.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    sources.push(nested as ApiRow);
  }

  for (const source of sources) {
    const total =
      typeof source.totalElements === 'number'
        ? source.totalElements
        : typeof source.total === 'number'
          ? source.total
          : null;
    const totalPages =
      typeof source.totalPages === 'number' && source.totalPages > 0 ? source.totalPages : null;
    if (total != null) {
      return {
        total,
        totalPages: totalPages ?? Math.max(1, Math.ceil(total / 1)),
      };
    }
  }
  return null;
}

function mapLetterTemplateListRow(row: ApiRow): LetterTemplateListRow | null {
  const id = pickStr(row, 'id', 'letter_template_id', 'letterTemplateId');
  const templateName = pickStr(
    row,
    'template_name',
    'templateName',
    'letter_template_name',
    'letterTemplateName',
    'name',
  );
  if (!id || !templateName) return null;

  const active = bool(row.status) ?? bool(row.active) ?? true;

  return {
    id,
    categoryId: pickStr(row, 'category_id', 'categoryId', 'file_category_id', 'fileCategoryId'),
    categoryName: pickStr(
      row,
      'category',
      'category_name',
      'categoryName',
      'file_category_name',
      'fileCategoryName',
    ),
    subCategoryId: pickStr(
      row,
      'sub_category_id',
      'subCategoryId',
      'sub_file_category_id',
      'subFileCategoryId',
    ),
    subCategoryName: pickStr(
      row,
      'sub_category',
      'sub_category_name',
      'subCategoryName',
      'sub_file_category_name',
      'subFileCategoryName',
    ),
    templateName,
    active,
    updatedAt: pickStr(
      row,
      'last_updated_at',
      'lastUpdatedAt',
      'updated_at',
      'updatedAt',
    ),
  };
}

function mapLetterTemplateDetail(row: ApiRow): LetterTemplateDetail | null {
  const list = mapLetterTemplateListRow(row);
  if (!list) return null;

  const letterContent = pickStr(
    row,
    'letter_content',
    'letterContent',
    'template_content',
    'templateContent',
    'html_content',
    'htmlContent',
  );

  return {
    ...list,
    code: pickStr(row, 'code', 'template_code', 'templateCode'),
    description: pickStr(row, 'description'),
    updatedBy: pickStr(
      row,
      'updated_by_name',
      'updatedByName',
      'updated_by',
      'updatedBy',
      'last_updated_by',
      'lastUpdatedBy',
    ),
    letterContent,
    pdfUrl: pickStr(
      row,
      'pdf_url',
      'pdfUrl',
      'template_file_url',
      'templateFileUrl',
      'file_url',
      'fileUrl',
      'document_url',
      'documentUrl',
    ),
    pdfBase64: pickStr(
      row,
      'pdf_base64',
      'pdfBase64',
      'template_content_base64',
      'templateContentBase64',
      'file_base64',
      'fileBase64',
    ),
    htmlContent: letterContent,
  };
}

/** Render plain-text or HTML letter template content for preview iframe. */
export function buildLetterTemplatePreviewDocument(content: string): string {
  const trimmed = content.trim();
  if (!trimmed) return '';

  if (/<html[\s>]/i.test(trimmed) || /<body[\s>]/i.test(trimmed)) {
    return trimmed;
  }

  const escaped = trimmed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return `<!DOCTYPE html><html lang="si"><head><meta charset="utf-8"><style>
    body {
      font-family: "Iskoola Pota", "Noto Sans Sinhala", Arial, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      padding: 32px 40px;
      margin: 0;
      white-space: pre-wrap;
      color: #111;
      background: #fff;
    }
  </style></head><body>${escaped}</body></html>`;
}

function parseLetterTemplatePage(
  data: unknown,
  page: number,
  pageSize: number,
): LetterTemplateListResult {
  const rows = unwrapRows(data)
    .map(mapLetterTemplateListRow)
    .filter((row): row is LetterTemplateListRow => row != null);

  const meta = pageMeta(data);
  if (meta) {
    const totalPages =
      meta.totalPages > 0
        ? meta.totalPages
        : Math.max(1, Math.ceil(meta.total / Math.max(1, pageSize)));
    return { items: rows, total: meta.total, totalPages };
  }

  const total = rows.length;
  const start = page * pageSize;
  return {
    items: rows.slice(start, start + pageSize),
    total,
    totalPages: Math.max(1, Math.ceil(total / Math.max(1, pageSize))),
  };
}

function buildListBody(params: LetterTemplateListParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const categoryId = params.categoryId?.trim();
  const subCategoryId = params.subCategoryId?.trim();
  const templateName = params.templateName?.trim();
  const sortBy = params.sortBy?.trim();
  const sortDir = params.sortDir?.trim();

  if (categoryId) body.category_id = categoryId;
  if (subCategoryId) body.sub_category_id = subCategoryId;
  if (templateName) body.template_name = templateName;
  if (typeof params.status === 'boolean') body.status = params.status;
  if (sortBy) body.sort_by = sortBy;
  if (sortDir) body.sort_direction = sortDir;

  return body;
}

/** POST /v1/workflow-templates/letter-templates/list */
export async function listLetterTemplates(
  params: LetterTemplateListParams,
): Promise<ApiResult<LetterTemplateListResult>> {
  const page = Math.max(0, params.page);
  const pageSize = Math.max(1, params.pageSize);
  const qs = new URLSearchParams({
    page: String(page),
    size: String(pageSize),
  });

  const res = await workflowApiRequest<unknown>(
    `/letter-templates/list?${qs.toString()}`,
    {
      method: 'POST',
      body: buildListBody(params),
      signal: params.signal,
    },
  );

  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: parseLetterTemplatePage(res.data, page, pageSize) };
}

export type LetterTemplateNameOption = {
  id: string;
  name: string;
};

/** GET /v1/workflow-templates/letter-templates?sub_category_id= */
export async function listLetterTemplatesBySubCategory(
  subCategoryId: string,
  signal?: AbortSignal,
): Promise<ApiResult<LetterTemplateNameOption[]>> {
  const id = subCategoryId.trim();
  if (!id) return { success: true, data: [] };

  const qs = new URLSearchParams({ sub_category_id: id });
  const res = await workflowApiRequest<unknown>(`/letter-templates?${qs.toString()}`, {
    method: 'GET',
    signal,
  });
  if (!res.ok) return { success: false, message: res.message };

  const rows = unwrapRows(res.data);
  const out: LetterTemplateNameOption[] = [];
  for (const item of rows) {
    if (!item || typeof item !== 'object') continue;
    const r = item as ApiRow;
    const templateId = pickStr(r, 'id', 'letter_template_id', 'letterTemplateId');
    const name = pickStr(
      r,
      'template_name',
      'templateName',
      'name',
      'letter_template_name',
      'letterTemplateName',
    );
    if (!templateId || !name) continue;
    out.push({ id: templateId, name });
  }
  out.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return { success: true, data: out };
}

/** @deprecated Use {@link listLetterTemplatesBySubCategory} when sub file category is known. */
export async function listLetterTemplateNameOptions(
  signal?: AbortSignal,
): Promise<ApiResult<string[]>> {
  const res = await listLetterTemplates({ page: 0, pageSize: 500, signal });
  if (!res.success) return { success: false, message: res.message };

  const seen = new Set<string>();
  const names: string[] = [];
  for (const row of res.data.items) {
    const name = row.templateName.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return { success: true, data: names };
}

/** GET /v1/workflow-templates/letter-templates/{id} */
export async function getLetterTemplateById(id: string): Promise<ApiResult<LetterTemplateDetail>> {
  const templateId = id.trim();
  if (!templateId) return { success: false, message: 'Letter template id is missing.' };

  const res = await workflowApiRequest<unknown>(
    `/letter-templates/${encodeURIComponent(templateId)}`,
    { method: 'GET' },
  );
  if (!res.ok) return { success: false, message: res.message };

  const detail = mapLetterTemplateDetail(unwrapRow(res.data));
  if (!detail) return { success: false, message: 'Letter template details are unavailable.' };
  return { success: true, data: detail };
}

/** PUT /v1/workflow-templates/letter-templates/{id} */
export async function updateLetterTemplate(
  id: string,
  input: UpdateLetterTemplateInput,
): Promise<ApiResult<void>> {
  const templateId = id.trim();
  const templateName = input.templateName.trim();
  if (!templateId) return { success: false, message: 'Letter template id is missing.' };
  if (!templateName) return { success: false, message: 'Letter template name is required.' };

  const res = await workflowApiRequest<unknown>(
    `/letter-templates/${encodeURIComponent(templateId)}`,
    {
      method: 'PUT',
      body: {
        template_name: templateName,
        status: input.active,
      },
    },
  );
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}
